import { useState } from "react";
import { RootDispatch, RootState } from "../../store";
import imageCompression from "browser-image-compression";
import { PDFDocument } from "pdf-lib";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  addPaymentDetails,
  creditPayment,
  fetchOrdersRoleBased,
} from "../../store/slices/orderSlice";
import OrdersSkeleton from "../../components/Skeleton/OrdersPageSkeleton";
import { motion } from "framer-motion";
import {
  Calendar,
  ChevronDown,
  Clock,
  FileText,
  Hash,
  LoaderCircle,
  PackageCheck,
  Truck,
  Upload,
  X,
} from "lucide-react";
import Button, { ButtonType } from "../../components/Assets/Button";
import SendToWhatsApp from "../../components/SendToWhatsapp/SendToWhatsApp";
import {
  IdispatchedQuantity,
  IOrder,
  IOrderGoods,
  IOrderValidation,
  IUTRDetails,
} from "../../models/Order.model";
import { IOrderCompanyInfo } from "../../models/epcCustomer.model";
import OrderCancelConfirmModal from "./OrderCancelConfirmModal";
import { HandPrintAndDownload } from "../../components/Assets/generateBill/HandlePrintAndDownload";
import { CompanyInfo } from "../../models/quotationData.model";
import { getInvoiceById } from "../../store/slices/einvoiceSlice";
import { InvoiceDocument } from "../../components/PaymentValidation/InvoiceDocument";
import { InvoiceData } from "../../models/invoice/invoice.model";
import { pdf } from "@react-pdf/renderer";

interface OrderCardProps {
  order: IOrder;
  activeTab: string;
  handleCancelOrder?: () => Promise<void>;
  searchTerm?: string;
}

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  activeTab,
  handleCancelOrder,
  searchTerm,
}) => {
  const [utr, setUTR] = useState("");
  const [utrPrice, setUtrPrice] = useState("");
  const [filePrice, setFilePrice] = useState("");

  const [utrNumbersArray] = useState<IUTRDetails[]>([]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileArray, setFileArray] = useState<{ file: File; price: string }[]>(
    [],
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const loading = useSelector((state: RootState) => state.order.loading);

  const dispatch: RootDispatch = useDispatch();
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [selectedCompanyInfo, setSelectedCompanyInfo] =
    useState<IOrderCompanyInfo>();
  const [showMobileNumberModal, setShowMobileNumberModal] = useState(false);
  const [mobileNumberInput, setMobileNumberInput] = useState(
    order.customerId?.mobileNumber,
  );
  const [selectedMethod, setSelectedMethod] = useState<"utr" | "upload" | null>(
    null,
  );
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [isViewingInvoice, setIsViewingInvoice] = useState(false);
  const [showInvoiceList, setShowInvoiceList] = useState(false);

  const transportPrice = order.transportPrice || 0;
  const discountPrice = order.discountPrice || 0;
  const finalOrderTotal = order.totalAmount || 0;

  const isCredited =
    order.orderStatus === "credited" || order.orderStatus === "Credited";
  const isPending = order.orderStatus === IOrderValidation.pending;
  const isApproved = order.orderStatus === IOrderValidation.approved;
  const createdByAndAuthorSame = Boolean(
    order.createdBy &&
    order.author &&
    (order.createdBy._id === order.author._id ||
      order.createdBy.username === order.author.username),
  );
  const orderTeam = [
    { label: "Created by", person: order.createdBy },
    { label: "Author", person: order.author },
    { label: "Sales rep", person: order.salesRep },
    { label: "CSE", person: order.cse },
  ].filter(
    ({ label, person }) =>
      person?.username && !(label === "Author" && createdByAndAuthorSame),
  );

  const handleViewInvoice = async (invoiceId: string) => {
    if (!invoiceId) return;

    const previewWindow = window.open("", "_blank");
    setIsViewingInvoice(true);
    try {
      const res = await dispatch(getInvoiceById({ invoiceId })).unwrap();
      if (!res?.success) {
        previewWindow?.close();
        return;
      }

      setShowInvoiceList(false);
      const invoiceData: InvoiceData = res.data;
      const blob = await pdf(
        <InvoiceDocument
          data={invoiceData}
          shippingAddress={order.shippingAddress}
        />,
      ).toBlob();
      const buyer =
        invoiceData.extractedInvoice?.BuyerDtls ||
        invoiceData.taxProResponse?.ExtractedSignedInvoiceData?.BuyerDtls;
      const customerName =
        (buyer?.LglNm || buyer?.TrdNm || "")
          .replace(/[<>:"/\\|?*]/g, "")
          .split("")
          .filter((character) => character.charCodeAt(0) >= 32)
          .join("")
          .trim()
          .replace(/\s+/g, "_") || "Customer";
      const invoiceNumber =
        invoiceData.invoiceNumber ||
        invoiceData.extractedInvoice?.DocDtls?.No ||
        "ORD";
      const fileName = `Invoice_${customerName}_${invoiceNumber}.pdf`;
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !("MSStream" in window);
      const fileURL = URL.createObjectURL(blob);

      if (previewWindow) {
        previewWindow.location.href = fileURL;
      } else if (isIOS) {
        window.location.href = fileURL;
      }

      if (!isIOS) {
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      window.setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
    } catch (error) {
      console.error("Failed to view invoice:", error);
      previewWindow?.close();
    } finally {
      setIsViewingInvoice(false);
    }
  };

  const invoiceRecords = order.invoice?.invoiceId ?? [];
  const canViewInvoices =
    ["GENERATED", "PARTIAL"].includes(order.invoice?.status || "") &&
    invoiceRecords.length > 0;
  const openInvoices = () => {
    if (invoiceRecords.length === 1) {
      void handleViewInvoice(invoiceRecords[0]._id);
    } else if (invoiceRecords.length > 1) {
      setShowInvoiceList(true);
    }
  };

  const [showGoodsModal, setShowGoodsModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);

  const handleCreditPayment = async () => {
    try {
      await dispatch(creditPayment(order._id)).unwrap();
      await dispatch(
        fetchOrdersRoleBased({ status: activeTab, search: searchTerm }),
      );
      setShowCreditConfirm(false);
    } catch (error) {
      console.error(error);
    }
  };
  const handleUTRSubmit = async () => {
    if (
      (!utr && !selectedFile) ||
      (utr && !utrPrice) ||
      (selectedFile && !filePrice)
    ) {
      toast.error("Please provide both payment method and amount");
      return;
    }

    const formData = new FormData();
    formData.append("orderId", order._id);

    if (utr && utrPrice) {
      const utrData = [{ utrNumber: utr, amount: parseFloat(utrPrice) }];
      formData.append("utrNumber", JSON.stringify(utrData));
    }

    if (selectedFile && filePrice) {
      formData.append("screenshotFile", selectedFile);
      formData.append("screenshotAmount", filePrice);
    }

    try {
      await dispatch(addPaymentDetails(formData));
      await dispatch(
        fetchOrdersRoleBased({ status: activeTab, search: searchTerm }),
      );
      setUtrPrice("");
      setFilePrice("");
      setSelectedFile(null);
      setUTR("");
      setFileArray([]);
    } catch (error) {
      toast.error("Failed to submit payment details");
    }
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 1 * 1024 * 1024;

    let finalFile = file;

    if (file.type.startsWith("image/") && file.size > maxSize) {
      const toastId = toast.loading("Compressing image...");

      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        finalFile = await imageCompression(file, options);
        toast.success("Image compressed successfully!", { id: toastId });
      } catch (err) {
        console.error("Image compression failed:", err);
        toast.error("Could not compress image—uploading original.", {
          id: toastId,
        });
      }
    }

    if (file.type === "application/pdf" && file.size > maxSize) {
      const toastId = toast.loading("Compressing PDF...");

      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const compressedBytes = new Uint8Array(
          await pdfDoc.save({
            useObjectStreams: true,
          }),
        );
        finalFile = new File([compressedBytes.buffer], file.name, {
          type: "application/pdf",
        });
        toast.success("PDF compressed successfully!", { id: toastId });
      } catch (err) {
        console.error("PDF compression failed:", err);
        toast.error("Could not compress PDF—uploading original.");
      }
    }

    if (finalFile.size > maxSize) {
      toast.error("File is still larger than 1 MB after compression.");
      return;
    }

    setSelectedFile(finalFile);
    setFileArray((prev) => [...prev, { file: finalFile, price: filePrice }]);
  };

  if (loading) {
    return <OrdersSkeleton />;
  }

  return (
    <>
      <motion.div
        className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm hover:shadow transition-all duration-200 w-full max-w-sm"
        whileHover={{ y: -1 }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <div className="bg-emerald-100 p-1.5 rounded-lg shrink-0">
                <PackageCheck size={18} className="text-emerald-600" />
              </div>

              <div className="min-w-0">
                <h3 className="text-xs font-semibold text-slate-800 truncate">
                  {order.customerId?.name || "Unknown Customer"}
                </h3>

                <p className="text-xs text-slate-500 mt-0.5">
                  {order?.customerId?.mobileNumber}
                </p>

                <p className="text-[11px] text-slate-400 mt-1">
                  {order.orderNumber}
                </p>
              </div>
            </div>

            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize bg-slate-100 text-slate-700 shrink-0">
              {order?.orderStatus || "Processing"}
            </span>
          </div>

          {orderTeam.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 rounded-md bg-gray-50 px-2 py-1.5">
              {orderTeam.map(({ label, person }) => (
                <div key={label} className="flex min-w-0 items-baseline gap-1">
                  <span className="shrink-0 text-[10px] text-gray-500">
                    {label}:
                  </span>
                  <span className="max-w-[130px] truncate text-[11px] font-medium text-gray-800">
                    {person?.username}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded-md font-medium">
            <span className="text-[11px] text-slate-700">Order Number:</span>
            <span className="text-[11px] text-slate-700 col-span-2">
              {order.orderNumber || "N/A"}
            </span>
          </div>

          <div className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded-md font-medium">
            <span className="text-[11px] text-slate-700">Created At</span>
            <span className="text-[11px] text-slate-700 col-span-2">
              {order.createdAt
                ? new Date(order.createdAt).toLocaleDateString()
                : new Date(order.createdDate).toLocaleDateString()}
            </span>
          </div>

          <div className="space-y-1">
            {order.goods.slice(0, 1).map((good: IOrderGoods) => {
              const quantity = good?.quantity || 0;
              const unit = good?.unit || "";
              const price = good?.price || 0;
              const discount = good?.discount || 0;
              const gstPercent = good?.gst || good?.product?.gst || 0;

              const baseAmount = price * quantity;
              const amountAfterDiscount = baseAmount - discount;
              const gstAmount = (amountAfterDiscount * gstPercent) / 100;

              return (
                <div
                  key={good._id}
                  className="flex flex-col gap-1 p-2 rounded-lg bg-slate-50 border border-slate-100"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-slate-600 truncate">
                      {good?.product?.productName}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-blue-500/90 font-medium">
                        ×{quantity} {unit}
                      </span>
                      <span className="text-[11px] text-slate-600 font-medium">
                        ₹{baseAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {discount > 0 && (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[10px] text-red-500 italic">
                        - Discount
                      </span>
                      <span className="text-[10px] text-red-500 font-medium">
                        ₹{discount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2">
                    <span className="text-[10px] text-slate-800">
                      + GST ({gstPercent}%)
                    </span>
                    <span className="text-[10px] text-slate-800">
                      ₹{gstAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })}
            {/* SHOW MORE BUTTON */}
            {order.goods.length > 2 && (
              <button
                onClick={() => setShowGoodsModal(true)}
                className="w-full py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-xs font-medium text-slate-600 transition-all"
              >
                View All Products ({order.goods.length})
              </button>
            )}
            {transportPrice > 0 && (
              <div className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded-md font-medium">
                <span className="text-[11px] text-slate-700">Transport</span>
                <span className="text-[11px] text-slate-700 col-span-2">
                  ₹{transportPrice.toFixed(2)}
                </span>
              </div>
            )}
            {discountPrice > 0 && (
              <div className="flex items-center justify-between p-1 bg-red-50 rounded font-medium border border-red-100">
                <span className="text-[11px] text-red-600">Discount</span>
                <span className="text-[11px] text-red-600 col-span-2">
                  - ₹{discountPrice.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded-md font-medium border-t border-slate-200 mt-1">
              <span className="text-[11px] text-slate-800 font-bold">
                Total
              </span>
              <span className="text-[11px] text-slate-800 font-bold col-span-2">
                ₹{finalOrderTotal.toFixed(2)}
              </span>
            </div>
          </div>

          {order.paymentDetails?.paymentPaid === 0 &&
            order.paymentDetails.paymentRemaining === 0 && (
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-50 px-2 py-1 text-[11px] rounded-lg flex justify-between">
                  <span className="text-slate-500">Paid</span>
                  <span className="font-semibold text-slate-800">
                    ₹{Number(order.paymentDetails.paymentPaid).toFixed(2)}
                  </span>
                </div>

                <div className="bg-slate-50 px-2 py-1 text-[11px] rounded-lg flex justify-between">
                  <span className="text-slate-500">Remaining</span>
                  <span className="text-[11px] text-slate-700 col-span-2">
                    ₹{finalOrderTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          {order.paymentDetails?.paymentPaid !== 0 &&
            order.paymentDetails.paymentRemaining !== 0 && (
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-50 px-2 py-1 text-[11px] rounded-lg flex justify-between">
                  <span className="text-slate-500">Paid</span>
                  <span className="font-semibold text-slate-800">
                    ₹{Number(order.paymentDetails.paymentPaid).toFixed(2)}
                  </span>
                </div>

                <div className="bg-slate-50 px-2 py-1 text-[11px] rounded-lg flex justify-between">
                  <span className="text-slate-500">Remaining</span>
                  <span className="text-[11px] text-slate-700 col-span-2">
                    ₹
                    {(
                      finalOrderTotal - order.paymentDetails.paymentPaid
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

          {/* PAYMENT INPUTS - VISIBLE FOR PENDING AND CREDITED */}
          {(isPending || isCredited) && (
            <div className="space-y-1.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                {[
                  { value: "utr", label: "UTR" },
                  { value: "upload", label: "Upload Screenshot/PDF" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition 
                    ${
                      selectedMethod === option.value
                        ? "border-blue-500 bg-blue-50 text-blue-600"
                        : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={option.value}
                      checked={selectedMethod === option.value}
                      onChange={() => {
                        setSelectedMethod(option.value as "utr" | "upload");
                        setShowCreditConfirm(false); // Reset credit toggles if changing methods
                      }}
                      className="hidden"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border flex items-center justify-center
                      ${
                        selectedMethod === option.value
                          ? "border-blue-500 bg-blue-500"
                          : "border-slate-400"
                      }`}
                    >
                      {selectedMethod === option.value && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <span className="font-medium">{option.label}</span>
                  </label>
                ))}
              </div>

              {selectedMethod === "utr" && (
                <div className="space-y-1.5">
                  <input
                    type="text"
                    value={utr}
                    onChange={(e) => setUTR(e.target.value)}
                    placeholder="UTR Number"
                    className="w-full px-2 py-1 text-[11px] border rounded-md text-xs placeholder-slate-400"
                  />
                  <input
                    type="number"
                    value={utrPrice}
                    onChange={(e) => setUtrPrice(e.target.value)}
                    placeholder="Amount"
                    className="w-full px-2 py-1 text-[11px] border rounded-md text-xs placeholder-slate-400"
                  />
                  <button
                    onClick={handleUTRSubmit}
                    disabled={loading}
                    className="
w-full h-8
bg-indigo-600 hover:bg-indigo-700
text-white text-sm font-medium
rounded-xl transition-all
disabled:opacity-50
"
                  >
                    Confirm
                  </button>
                </div>
              )}

              {selectedMethod === "upload" && (
                <div className="space-y-1.5">
                  <label className="flex items-center justify-center gap-2 p-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer text-xs text-slate-600 hover:border-indigo-400 hover:bg-indigo-50 transition">
                    <Upload className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">Upload Screenshot/PDF</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                    />
                  </label>
                  {selectedFile && (
                    <>
                      <input
                        type="number"
                        value={filePrice}
                        onChange={(e) => setFilePrice(e.target.value)}
                        placeholder="Amount"
                        className="w-full px-2 py-1 text-[11px] border rounded-md text-xs placeholder-slate-400"
                      />
                      <button
                        onClick={handleUTRSubmit}
                        disabled={loading}
                        className="
w-full h-8
bg-indigo-600 hover:bg-indigo-700
text-white text-sm font-medium
rounded-xl transition-all
disabled:opacity-50
"
                      >
                        Confirm
                      </button>
                    </>
                  )}
                  {/* ... File Preview Grid ... */}
                  <div className="grid grid-cols-8 gap-1">
                    {fileArray.map((file, index) => (
                      <div key={index} className="relative aspect-square">
                        {file.file.type === "application/pdf" ? (
                          <div className="w-full self-start bg-red-50 rounded-lg border border-red-200 flex flex-col items-center justify-center p-1">
                            <FileText className="text-red-500" size={16} />
                            <span className="text-[0.5rem] text-red-600 text-center truncate w-full">
                              {file.file.name}
                            </span>
                          </div>
                        ) : (
                          <img
                            src={URL.createObjectURL(file.file)}
                            alt={`Payment ${index + 1}`}
                            className="w-full self-start object-cover rounded-lg border border-slate-200"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CREDIT BUTTON SECTION - ONLY SHOW FOR PENDING ORDERS */}
              {isPending && (
                <div className="pt-2 border-t border-slate-100">
                  {!showCreditConfirm ? (
                    <button
                      onClick={() => setShowCreditConfirm(true)}
                      className="w-full py-1.5 border border-indigo-200 text-indigo-600 bg-indigo-50 rounded-md text-xs font-medium hover:bg-indigo-100 transition-colors"
                    >
                      Give on Credit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreditPayment}
                        disabled={loading}
                        className="flex-1 bg-emerald-500 text-white py-1.5 rounded-md text-xs hover:bg-emerald-600 transition-colors disabled:opacity-50"
                      >
                        {loading ? "Processing..." : "Confirm"}
                      </button>
                      <button
                        onClick={() => setShowCreditConfirm(false)}
                        disabled={loading}
                        className="flex-1 bg-slate-200 text-slate-600 py-1.5 rounded-md text-xs hover:bg-slate-300 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {canViewInvoices && (
            <button
              type="button"
              onClick={openInvoices}
              disabled={isViewingInvoice}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-700 transition-colors hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isViewingInvoice ? (
                <LoaderCircle className="animate-spin" size={14} />
              ) : (
                <FileText size={14} />
              )}
              {invoiceRecords.length > 1 ? "View invoices" : "View invoice"}
            </button>
          )}

          {/* VIEW PAYMENT DETAILS - PENDING (if partial), APPROVED, CREDITED */}
          {(isApproved || isCredited || order?.paymentDetails?.payment) && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="mt-4 w-full text-xs bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 transition-colors"
            >
              See Payment Details
            </button>
          )}

          {/* Open PI BUTTON - PENDING OR CREDITED */}
          {(isPending || isCredited) && (
            <Button
              type={ButtonType.Submit}
              onClick={async () => {
                const products = order.goods.map((item: IOrderGoods) => ({
                  gst: item.gst || item.product?.gst || 0,
                  hsn: item.hsn || item.product?.hsn || 0,
                  productName: item.product?.productName || "Unknown Product",
                  quantity: item.quantity || 0,
                  price: item.price || 0,
                  discount: item.discount || 0,
                }));

                const companyInfo: CompanyInfo = {
                  name: order.customerId?.name || "",
                  email: order.customerId?.email || "",
                  mobileNumber: order.customerId?.mobileNumber || "",
                  billingAddress: order.customerId?.billingAddress?.line1 || "",
                  billingCity: order.customerId?.billingAddress?.city || "",
                  billingState: order.customerId?.billingAddress?.state || "",
                  billingPincode:
                    order.customerId?.billingAddress?.pincode || "",
                  contactPersonName: order.customerName,
                };
                HandPrintAndDownload(
                  products,
                  companyInfo,
                  transportPrice,
                  discountPrice,
                  true,
                );
                setSelectedProducts(products);
                setMobileNumberInput(order.customerId?.mobileNumber || "");
              }}
              className="w-full  text-xs rounded-lg mt-2"
            >
              Open PI
            </Button>
          )}

          {utrNumbersArray.length > 0 && (
            <div className="mt-2 space-y-1">
              {utrNumbersArray.map((item, index) => (
                <div
                  key={index}
                  className="text-sm text-gray-700 bg-gray-100 px-3 py-1 rounded-md"
                >
                  UTR: {item?.utr}
                </div>
              ))}
            </div>
          )}

          {/* CANCEL ORDER - PENDING, APPROVED, OR CREDITED */}
          {(isPending || isApproved || isCredited) && (
            <Button
              type={ButtonType.Error}
              onClick={() => setShowCancelConfirm(true)}
              className="w-full text-[11px] mt-1 h-8"
            >
              Cancel Order
            </Button>
          )}

          <div className="space-y-2 mt-2">
            {order.orderStatus?.toLowerCase() !== "pending" &&
            order.dispatchQuantity?.length > 0 ? (
              <div className="space-y-2">
                {order.dispatchQuantity
                  .slice(0, 1)
                  .map((dispatchItem: IdispatchedQuantity, idx: number) => (
                    <details
                      key={idx}
                      className="group bg-white rounded-lg border border-slate-200 overflow-hidden hover:border-slate-300 transition-all"
                    >
                      <summary className="cursor-pointer list-none px-3 py-1.5 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-1 bg-emerald-50 rounded border border-emerald-100/50">
                            <PackageCheck
                              size={14}
                              className="text-emerald-600"
                            />
                          </div>
                          <p className="text-sm font-semibold text-slate-700 leading-tight">
                            {dispatchItem?.productName ||
                              dispatchItem?.name ||
                              "Item"}
                          </p>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                            {dispatchItem.serialNumbersWithDate?.length || 0}{" "}
                            Serials
                          </span>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 transition-transform duration-200 group-open:rotate-180" />
                        </div>
                      </summary>

                      <div className="border-t border-slate-100">
                        {order.trackingDetails &&
                        order.trackingDetails.length > 0 ? (
                          (() => {
                            const track = order.trackingDetails[0];
                            return (
                              <div className="px-4 py-1.5 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <Truck size={12} className="text-slate-400" />
                                  <span className="text-[10px] font-medium text-slate-600">
                                    Tracking:{" "}
                                    <span className="text-slate-800 font-mono">
                                      {track.trackingNumber || "N/A"}
                                    </span>
                                  </span>
                                </div>
                                {track.trackingLink && (
                                  <a
                                    href={track.trackingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] font-bold text-blue-600 hover:underline"
                                  >
                                    Track
                                  </a>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <div className="px-4 py-1.5 bg-slate-50/80 border-b border-slate-100 flex items-center gap-2">
                            <Truck size={12} className="text-slate-300" />
                            <span className="text-[10px] text-slate-400 italic">
                              No tracking info
                            </span>
                          </div>
                        )}

                        <div className="p-2 bg-white">
                          <div className="grid grid-cols-2 gap-2">
                            {dispatchItem.serialNumbersWithDate?.map(
                              (item: any, i: number) => (
                                <div
                                  key={i}
                                  className={`flex flex-col px-2 py-1 rounded border ${
                                    item.dispatchedDate
                                      ? "bg-white border-slate-200"
                                      : "bg-slate-50 border-dashed border-slate-300"
                                  }`}
                                >
                                  <div className="flex items-center gap-1.5 overflow-hidden">
                                    <Hash
                                      size={10}
                                      className="text-slate-400 shrink-0"
                                    />
                                    <span className="font-mono text-[10px] font-bold text-slate-700 truncate">
                                      {item.sn}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-1 text-[9px] text-slate-500">
                                    {item.dispatchedDate ? (
                                      <>
                                        <Calendar
                                          size={9}
                                          className="text-emerald-500/70"
                                        />
                                        <span>
                                          {new Date(
                                            item.dispatchedDate,
                                          ).toLocaleDateString("en-IN")}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="italic text-amber-600">
                                        Pending
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    </details>
                  ))}
                {order.dispatchQuantity?.length > 1 && (
                  <button
                    onClick={() => setShowDispatchModal(true)}
                    className="
      w-full mt-2 py-1.5 rounded-lg
      border border-slate-200
      bg-slate-50 hover:bg-slate-100
      text-xs font-medium text-slate-600
      transition-all
    "
                  >
                    View Dispatch Details ({order.dispatchQuantity.length})
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 rounded bg-slate-50 border border-slate-100 text-slate-400">
                <Clock size={14} />
                <p className="text-[11px]">No dispatch data available.</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
      {showInvoiceList && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
          onClick={() => setShowInvoiceList(false)}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`invoice-list-title-${order._id}`}
            initial={{ scale: 0.95, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h3
                  id={`invoice-list-title-${order._id}`}
                  className="text-base font-semibold text-slate-900"
                >
                  Invoices for order {order.orderNumber}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Select an invoice to open or download.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close invoice list"
                onClick={() => setShowInvoiceList(false)}
                className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={16} />
              </button>
            </header>
            <div className="max-h-[60vh] space-y-2 overflow-y-auto p-4">
              {invoiceRecords.map((invoice, index) => (
                <button
                  key={invoice._id}
                  type="button"
                  disabled={isViewingInvoice || !invoice._id}
                  onClick={() => void handleViewInvoice(invoice._id)}
                  className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                    {isViewingInvoice ? (
                      <LoaderCircle className="animate-spin" size={18} />
                    ) : (
                      <FileText size={18} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-900">
                      {invoice.invoiceNumber || `Invoice ${index + 1}`}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {new Date(invoice.createdAt).toLocaleDateString("en-IN")}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
      {isModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setIsModalOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="
bg-white rounded-3xl
max-w-2xl w-full
shadow-2xl
max-h-[90vh]
overflow-y-auto
border border-slate-200
"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-slate-800">
                  Payment Details
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="space-y-4">
                {Object.entries(order?.paymentDetails?.utrNumber || {}).length >
                  0 && (
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">
                      UTR Numbers
                    </h4>
                    <div className="">
                      {Object.entries(
                        order?.paymentDetails?.utrNumber || {},
                      ).map(([utr, data]: [string, any]) => (
                        <div
                          key={utr}
                          className="py-1 px-2 bg-slate-50 rounded-lg border border-slate-100"
                        >
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-mono text-slate-700 break-all">
                              UTR: {utr}
                            </p>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                data.isCompleted
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-orange-100 text-yellow-800"
                              }`}
                            >
                              {data.isCompleted ? "Verified" : "Pending"}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600">
                            <p>Amount: ₹{Number(data.amount).toFixed(2)}</p>
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Object.entries(order?.paymentDetails?.paymentScreenshot || {})
                  .length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">
                      Payment Screenshots
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {Object.entries(
                        order?.paymentDetails?.paymentScreenshot || {},
                      ).map(([key, data]: [string, any]) => (
                        <div
                          key={key}
                          className="relative group flex flex-col justify-between"
                        >
                          {data.fileType === "pdf" ? (
                            <div className="w-full h-24 bg-red-50 rounded-lg border border-red-200 flex items-center justify-center">
                              <FileText className="text-red-500" size={16} />
                              <span className="text-xs text-red-600 truncate w-full">
                                {data.originalName}
                              </span>
                            </div>
                          ) : (
                            <img
                              src={data.url}
                              alt="Payment screenshot"
                              className="w-full h-24 rounded-lg border border-slate-200 object-cover"
                            />
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <a
                              href={data.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-white text-xs bg-navyBlue-primary px-2 py-1 rounded"
                            >
                              View Full
                            </a>
                          </div>
                          <div className="mt-1 text-xs text-slate-600">
                            <p>Amount: ₹{Number(data.amount).toFixed(2)}</p>
                            <p
                              className={`${
                                data.isCompleted
                                  ? "text-emerald-600"
                                  : "text-yellow-600"
                              }`}
                            >
                              {data.isCompleted ? "Verified" : "Pending"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Order Date:</span>
                    <span>
                      {new Date(order.createdDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-slate-500">Total Amount:</span>
                    <span className="font-medium">
                      ₹{finalOrderTotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-slate-500">Paid Amount:</span>
                    <span className="font-medium">
                      ₹{Number(order.paymentDetails.paymentPaid).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-slate-500">Remaining:</span>
                    <span className="font-medium">
                      ₹
                      {(
                        finalOrderTotal -
                        Number(order.paymentDetails.paymentPaid)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      <OrderCancelConfirmModal
        open={showCancelConfirm}
        title="Cancel Order?"
        description="Are you sure you want to cancel this order? This action cannot be undone."
        confirmText="Confirm"
        cancelText="Cancel"
        loading={loading}
        onCancel={() => setShowCancelConfirm(false)}
        onConfirm={async () => {
          if (handleCancelOrder) {
            await handleCancelOrder();
          }
          setShowCancelConfirm(false);
        }}
      />
      {showMobileNumberModal && (
        <SendToWhatsApp
          mobileNumberInput={mobileNumberInput ?? ""}
          setMobileNumberInput={setMobileNumberInput}
          currentLead={order}
          selectedCompanyInfo={selectedCompanyInfo}
          setSelectedCompanyInfo={setSelectedCompanyInfo}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          setShowMobileNumberModal={setShowMobileNumberModal}
          showMobileNumberModal={showMobileNumberModal}
          discountPrice={order.discountPrice || 0}
          transportPrice={order.transportPrice || 0}
          type="pi"
        />
      )}
      {showGoodsModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowGoodsModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="
        bg-white rounded-3xl
        w-full max-w-3xl
        max-h-[85vh]
        overflow-y-auto
        shadow-2xl
      "
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">
                Order Products
              </h2>

              <button
                onClick={() => setShowGoodsModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {order.goods.map((good: IOrderGoods) => {
                const quantity = good?.quantity || 0;
                const unit = good?.unit || "";
                const price = good?.price || 0;
                const discount = good?.discount || 0;
                const gstPercent = good?.gst || good?.product?.gst || 0;

                const baseAmount = price * quantity;
                const amountAfterDiscount = baseAmount - discount;
                const gstAmount = (amountAfterDiscount * gstPercent) / 100;

                return (
                  <div
                    key={good._id}
                    className="
                border border-slate-200
                rounded-lg p-4
                bg-slate-50
              "
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-800">
                          {good?.product?.productName}
                        </h3>

                        <p className="text-sm text-slate-500 mt-1">
                          Qty: {quantity} {unit}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="font-bold text-slate-800">
                          ₹{baseAmount.toFixed(2)}
                        </p>

                        {discount > 0 && (
                          <p className="text-sm text-red-500">
                            Discount: ₹{discount.toFixed(2)}
                          </p>
                        )}

                        <p className="text-sm text-slate-500">
                          GST ({gstPercent}%): ₹{gstAmount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
      {showDispatchModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowDispatchModal(false)}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="
        bg-white rounded-3xl
        w-full max-w-5xl
        max-h-[90vh]
        overflow-hidden
        shadow-2xl
        border border-slate-200
        flex flex-col
      "
          >
            {/* HEADER */}
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  Dispatch Details
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {order.dispatchQuantity?.length || 0} dispatch item(s)
                </p>
              </div>

              <button
                onClick={() => setShowDispatchModal(false)}
                className="
            p-2 rounded-xl
            hover:bg-slate-100
            transition-colors
          "
              >
                <X size={18} className="text-slate-600" />
              </button>
            </div>

            {/* BODY */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-slate-50">
              {order.dispatchQuantity.map(
                (dispatchItem: IdispatchedQuantity, idx: number) => (
                  <div
                    key={idx}
                    className="
                bg-white
                border border-slate-200
                rounded-3xl
                overflow-hidden
                shadow-sm
              "
                  >
                    {/* CARD HEADER */}
                    <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-slate-800">
                            {dispatchItem.productName ||
                              dispatchItem?.name ||
                              "Item"}
                          </h3>

                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span
                              className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                                dispatchItem.type === "material"
                                  ? "bg-orange-100 text-orange-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {dispatchItem.type === "material"
                                ? "Material"
                                : "Product"}
                            </span>

                            <span className="text-[10px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
                              Qty: {dispatchItem.quantity || 0}
                            </span>

                            <span className="text-[10px] px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                              {dispatchItem.serialNumbersWithDate?.length || 0}{" "}
                              Serials
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* TRACKING */}
                    {order.trackingDetails &&
                    order.trackingDetails.length > 0 ? (
                      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Truck size={14} className="text-slate-400" />

                          <span className="text-xs text-slate-600">
                            Tracking:
                          </span>

                          <span className="font-mono text-xs font-semibold text-slate-800">
                            {order.trackingDetails[0]?.trackingNumber || "N/A"}
                          </span>
                        </div>

                        {order.trackingDetails[0]?.trackingLink && (
                          <a
                            href={order.trackingDetails[0]?.trackingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="
                        text-xs font-semibold
                        text-blue-600 hover:text-blue-700
                        hover:underline
                      "
                          >
                            Track Shipment
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                        <p className="text-xs text-slate-400 italic">
                          No tracking details available
                        </p>
                      </div>
                    )}

                    {/* SERIAL GRID */}
                    <div className="p-5">
                      {dispatchItem.serialNumbersWithDate?.length ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {dispatchItem.serialNumbersWithDate.map(
                            (item: any, i: number) => (
                              <div
                                key={i}
                                className="rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-2 shadow-sm min-h-[70px] flex flex-col justify-between hover:border-slate-300 transition-all"
                              >
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <Hash
                                    size={13}
                                    className="text-slate-400 shrink-0"
                                  />

                                  <p className="font-mono text-sm font-bold text-slate-700 truncate">
                                    {item.sn}
                                  </p>
                                </div>

                                <div className="mt-3">
                                  {item.dispatchedDate ? (
                                    <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                                      <Calendar size={11} />

                                      <span>
                                        {new Date(
                                          item.dispatchedDate,
                                        ).toLocaleDateString("en-IN")}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-amber-600 italic">
                                      Dispatch Pending
                                    </div>
                                  )}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      ) : (
                        <div className="py-10 flex items-center justify-center">
                          <p className="text-sm text-slate-400 italic">
                            No serial numbers available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default OrderCard;
